import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "../../../customComponents/Footer";
import "./financialInstitution.css";

const FEATURES_10 = [
  {
    number: "01",
    icon: "/images/financial-institution-img/financial_exchange_icon.png",
    title: "Federally licensed",
    description: "Licensed by the UAE Capital Market Authority (SCA) to operate a virtual asset trading facility. VARA licence in progress.",
  },
  {
    number: "02",
    icon: "/images/financial-institution-img/financial_exchange_icon2.png",
    title: "Your money, kept apart from ours",
    description: "Client funds are held in accounts separate from company funds, and never mixed.",
  },
  {
    number: "03",
    icon: "/images/financial-institution-img/financial_exchange_icon3.png",
    title: "90% in cold storage",
    description: "Assets are held by a UAE-approved third-party custodian, with 90% offline and out of reach of online attacks.",
  },
  {
    number: "04",
    icon: "/images/financial-institution-img/financial_exchange_icon4.png",
    title: "A full audit trail",
    description: "Every deposit and withdrawal is logged end to end and tied to the client who made it.",
  },
  {
    number: "05",
    icon: "/images/financial-institution-img/financial_exchange_icon5.png",
    title: "Travel Rule compliant",
    description: "Qualifying transfers carry the information counterparties and regulators require.",
  },
  {
    number: "06",
    icon: "/images/financial-institution-img/financial_exchange_icon6.png",
    title: "Verified in minutes",
    description: "Automated KYC gets you from sign-up to trading fast.",
  },
  {
    number: "07",
    icon: "/images/financial-institution-img/financial_exchange_icon7.png",
    title: "Partnered with USDC and USDT",
    description: "Direct access to the world’s two leading stablecoins, with on-chain settlement 24/7",
  },
  {
    number: "08",
    icon: "/images/financial-institution-img/financial_exchange_icon8.png",
    title: "Bank transfers that credit themselves",
    description: "A dedicated deposit IBAN for every client, so deposits are recognised automatically, with no reference codes.",
  },
  {
    number: "09",
    icon: "/images/financial-institution-img/financial_exchange_icon9.png",
    title: "Same-day settlement for institutions",
    description: "Institutional withdrawals settle the same day, weekends included.",
  },
  {
    number: "10",
    icon: "/images/financial-institution-img/financial_exchange_icon10.png",
    title: "A licensed launchpad",
    description: "New crypto projects come to market through a regulated venue, not an anonymous one.",
  },
];

const VENUE_STEPS = [
  {
    number: "01",
    title: "Spot trading",
    description: "A full order book across major pairs, with the depth and order types active traders expect",
    image: "/images/financial-institution-img/crypto_exchange_img.png",
  },
  {
    number: "02",
    title: "Buy and sell",
    description: "Simple, instant purchases for first-time and everyday buyers",
    image: "/images/financial-institution-img/crypto_exchange_img2.png",
  },
  {
    number: "03",
    title: "Stablecoins",
    description: "Move between fiat, USDC and USDT, and settle on-chain around the clock",
    image: "/images/financial-institution-img/crypto_exchange_img3.png",
  },
  {
    number: "04",
    title: "Staking",
    description: "Earn rewards on supported assets from the same account. Rewards are variable and not guaranteed",
    image: "/images/financial-institution-img/crypto_exchange_img4.png",
  },
  {
    number: "05",
    title: "Launchpad",
    description: "A licensed route to market for new crypto projects",
    image: "/images/financial-institution-img/crypto_exchange_img.png",
  },
  {
    number: "06",
    title: "Institutional desk",
    description: "Dedicated onboarding, same-day settlement and Travel Rule-compliant transfers for funds, desks and brokers",
    image: "/images/financial-institution-img/crypto_exchange_img2.png",
  },
];

const USE_CASES = [
  {
    icon: "/images/financial-institution-img/crypto_financial_vector.png",
    title: "For individuals",
    description: "Verified in minutes, with free bank deposits and secure custody from day one.",
    linkText: "Get started",
    linkUrl: "/signup",
  },
  {
    icon: "/images/financial-institution-img/crypto_financial_vector2.png",
    iconLight: "/images/financial-institution-img/crypto_financial_vector2_light.png",
    title: "For institutions",
    description: "Separate client accounts, same-day settlement, Travel Rule compliance and a full audit trail.",
    linkText: "See institutional benefits",
    linkUrl: "/kyb",
  },
  {
    icon: "/images/financial-institution-img/crypto_financial_vector3.png",
    iconLight: "/images/financial-institution-img/crypto_financial_vector3_light.png",
    title: "For crypto projects",
    description: "Launch through a licensed launchpad on a regulated exchange.",
    linkText: "Apply to launch ",
    linkUrl: "/api-document",
  },
];

export default function FinancialInstitution() {
  const [activeVenue, setActiveVenue] = useState(0);
  const [stickyStyle, setStickyStyle] = useState({});
  const itemRefs = useRef([]);
  const gridRef = useRef(null);
  const visualColRef = useRef(null);
  const stickyBoxRef = useRef(null);

  useEffect(() => {
    // Preload images for instantaneous transitions
    VENUE_STEPS.forEach((step) => {
      const img = new Image();
      img.src = step.image;
    });

    const handleScroll = () => {
      // 1. Calculate active item closest to viewport center
      if (itemRefs.current && itemRefs.current.length > 0) {
        const centerY = window.innerHeight / 2;
        let closestIndex = 0;
        let minDistance = Infinity;

        itemRefs.current.forEach((el, index) => {
          if (!el) return;
          const rect = el.getBoundingClientRect();
          const elCenter = rect.top + rect.height / 2;
          const distance = Math.abs(centerY - elCenter);
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        });

        setActiveVenue((prev) => (prev !== closestIndex ? closestIndex : prev));
      }

      // 2. JavaScript Pinning Engine (100% Guaranteed Sticky Behavior)
      if (!gridRef.current || !visualColRef.current) return;

      if (window.innerWidth < 992) {
        setStickyStyle({});
        return;
      }

      const gridRect = gridRef.current.getBoundingClientRect();
      const colRect = visualColRef.current.getBoundingClientRect();
      const stickyHeight = stickyBoxRef.current ? stickyBoxRef.current.offsetHeight : 450;
      const topOffset = 100;

      if (gridRect.top <= topOffset) {
        if (gridRect.bottom <= topOffset + stickyHeight) {
          // Released at bottom
          setStickyStyle({
            position: "absolute",
            bottom: "0px",
            top: "auto",
            left: "0px",
            width: "100%",
          });
        } else {
          // Pinned on screen at top: 100px
          setStickyStyle({
            position: "fixed",
            top: `${topOffset}px`,
            left: `${colRect.left}px`,
            width: `${colRect.width}px`,
            height: "calc(100vh - 160px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          });
        }
      } else {
        // Before section
        setStickyStyle({
          position: "relative",
          top: "0px",
          width: "100%",
        });
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const scrollToStep = (index) => {
    if (itemRefs.current[index]) {
      itemRefs.current[index].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <div className="fin-inst-page-wrapper">
      <Helmet>
        <title>Financial Institution | AGCX Crypto Exchange</title>
        <meta
          name="description"
          content="The crypto exchange built like a financial institution. Regulated venue, Tier-1 UAE banking rails, MPC cold custody, and deep liquidity."
        />
      </Helmet>

      {/* ==========================================================================
          1. HERO SECTION
          ========================================================================== */}
      <section className="fin-inst-hero-section">
        {/* Floating Ambient Vectors */}
        <div className="fin-inst-hero-decor-1 herovector">
          <img className="light_img" src="/images/financial-institution-img/herobnr_vector.png" alt="" />
          <img className="dark_img" src="/images/financial-institution-img/herobnr_vector_light.png" alt="" />
        </div>
        <div className="fin-inst-hero-decor-2 herovector">
          <img className="light_img" src="/images/financial-institution-img/herobnr_vector2.png" alt="" />
          <img className="dark_img" src="/images/financial-institution-img/herobnr_vector2_light.png" alt="" />
        </div>
        <div className="fin-inst-hero-decor-3 herovector">
          <img className="light_img" src="/images/financial-institution-img/herobnr_vector3.png" alt="" />
          <img className="dark_img" src="/images/financial-institution-img/herobnr_vector3_light.png" alt="" />
        </div>
        <div className="fin-inst-hero-decor-4 herovector">
          <img className="light_img" src="/images/financial-institution-img/herobnr_vector4.png" alt="" />
          <img className="dark_img" src="/images/financial-institution-img/herobnr_vector4_light.png" alt="" />
        </div>

        <div className="fin-inst-container">
          <div className="fin-inst-hero-content">
            <div className="fin-inst-hero-badge">
              <span>Global Crypto Exchnage</span>
            </div>

            <h1 className="fin-inst-hero-title">
              The crypto exchange built
              <br />
              like a financial institution.
            </h1>

            <p className="fin-inst-hero-subtitle">
              Federally licensed, backed by decades of regulated-market experience, and built on the basics most exchanges skip: secure custody, separate client accounts and a full audit trail on every transaction.
            </p>

            <div className="fin-inst-hero-actions">
              <Link to="/signup" className="fin-inst-btn-primary" id="fin-inst-hero-signup-btn">
                <span>Open an account</span>
              </Link>
              <Link to="/kyb" className="fin-inst-btn-secondary" id="fin-inst-hero-explore-btn">
                <span>Talk to the institutional desk</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          2. EVERYTHING AN EXCHANGE SHOULD BE (10 FEATURES GRID)
          ========================================================================== */}
      <section className="fin-inst-features-section">
        <div className="fin-inst-container">
          <div className="fin-inst-section-header">
            <h2 className="fin-inst-section-title">
              Everything an exchange
              <br />
              should be. Nothing it shouldn’t.
            </h2>
            <p className="subtext">A grid of ten tiles, each with a title and one line:</p>
          </div>

          <div className="fin-inst-features-grid">
            {FEATURES_10.map((item, index) => (
              <div className="fin-inst-feature-card" key={index} id={`fin-inst-feature-${index + 1}`}>
                <div className="fin-inst-feature-after">
                  <img src="/images/financial-institution-img/financial_exchange_cards_after.png" alt={item.title} className="fin-inst-after-img" />
                </div>
                <div className="fin-inst-feature-card-top">
                  <div className="fin-inst-feature-icon-box">
                    <img src={item.icon} alt={item.title} className="fin-inst-feature-icon" />
                  </div>
                </div>
                <span className="fin-inst-feature-num">{item.number}</span>
                <h3 className="fin-inst-feature-title">{item.title}</h3>
                <p className="fin-inst-feature-desc">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==========================================================================
          3. THE MOST IMPORTANT FEATURE (MPC & CUSTODY)
          ========================================================================== */}
      <section className="fin-inst-custody-section">
        <div className="fin-inst-container">
          <div className="fin-inst-custody-grid">
            <div className="fin-inst-custody-text-col">
              <h2 className="fin-inst-custody-title">
                The most important feature
                <br />
                is the one you’ll never see
              </h2>
              <p className="fin-inst-custody-desc">
                In traditional finance, the firm that takes your order isn’t the firm that holds your assets, and that
                separation is what keeps investors safe. Many crypto exchanges hold the keys, run the trading and
                keep the books all in one place, which leaves a single point of failure.
              </p>
              <p className="fin-inst-custody-desc">
                AGCX is built the other way. Client crypto assets are held by an independent, UAE-approved third-party
                custodian, not by AGCX alone. 90% sits in
                cold storage, client and company money never mix, and every movement leaves an audit trail.
              </p>
            </div>

            <div className="fin-inst-custody-img-col">
              <div className="fin-inst-custody-img-wrapper">
                <img
                  src="/images/financial-institution-img/feature_exchange_vector.png"
                  alt="Institutional MPC Security Architecture"
                  className="fin-inst-custody-img light_img"
                />
                <img
                  src="/images/financial-institution-img/feature_exchange_vector_light.png"
                  alt="Institutional MPC Security Architecture"
                  className="fin-inst-custody-img dark_img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          4. NEW TO CRYPTO. NOT TO MARKETS. (MARKETS EXPERIENCE)
          ========================================================================== */}
      <section className="fin-inst-markets-section">
        <div className="fin-inst-container">
          <div className="fin-inst-markets-grid">
            <div className="fin-inst-markets-img-col">
              <div className="fin-inst-markets-img-wrapper">
                <img
                  src="/images/financial-institution-img/markets_exchange_vector.png"
                  alt="Market depth and trading charts"
                  className="fin-inst-markets-img light_img"
                />
                <img
                  src="/images/financial-institution-img/markets_exchange_vector_light.png"
                  alt="Market depth and trading charts"
                  className="fin-inst-markets-img dark_img"
                />
              </div>
            </div>

            <div className="fin-inst-markets-text-col">
              <h2 className="fin-inst-markets-title">
                New to crypto. Not to
                <br />
                markets.
              </h2>
              <p className="fin-inst-markets-desc">
                AGCX is part of a financial group that has operated in regulated markets for decades. Our
                founder has been a Chartered Accountant since 1987 and built a SEBI-regulated broker in India.
                Since 2005, the group has been a broker and clearing member of the Dubai Gold & Commodities Exchange,
                the Middle East’s largest derivatives
                exchange, with a presence in ADGM. AGCX brings that discipline to digital assets.
              </p>

              <div className="fin-inst-markets-stats-strip">
                <div className="fin-inst-market-stat-item">
                  <div className="fin-inst-stat-icon-box">
                    <img
                      src="/images/financial-institution-img/calender_icon.png"
                      alt="Calendar icon"
                      className="fin-inst-stat-icon"
                    />
                  </div>
                  <div className="fin-inst-stat-content">
                    <h4 className="fin-inst-stat-title">~40 years</h4>
                    <p className="fin-inst-stat-sub">in regulated markets</p>
                  </div>
                </div>

                <div className="fin-inst-market-stat-item">
                  <div className="fin-inst-stat-icon-box">
                    <img
                      src="/images/financial-institution-img/bank_icon.png"
                      alt="Bank icon"
                      className="fin-inst-stat-icon"
                    />
                  </div>
                  <div className="fin-inst-stat-content">
                    <h4 className="fin-inst-stat-title">
                      DGCX
                      <br />
                      member
                    </h4>
                    <p className="fin-inst-stat-sub">since 2005</p>
                  </div>
                </div>

                <div className="fin-inst-market-stat-item">
                  <div className="fin-inst-stat-icon-box">
                    <img
                      src="/images/financial-institution-img/location_icon.png"
                      alt="Location icon"
                      className="fin-inst-stat-icon"
                    />
                  </div>
                  <div className="fin-inst-stat-content">
                    <h4 className="fin-inst-stat-title">Headquartered in Dubai</h4>
                    <p className="fin-inst-stat-sub">
                      the world's #2 crypto market(Henley Crypto
                      Adoption Index 2026)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          5. ONE REGULATED VENUE FOR EVERYTHING (SCROLL STORYTELLING)
          ========================================================================== */}
      <section className="crypto-story">
        <div className="crypto-story-heading">
          <h2 className="fin-inst-section-title">
            One regulated venue for
            <br />
            everything <span className="fin-inst-text-gold">you do in crypto.</span>
          </h2>
        </div>

        <div className="crypto-story-grid" ref={gridRef}>
          <div className="crypto-story-content">
            {VENUE_STEPS.map((step, idx) => (
              <div
                key={idx}
                className={`story-item ${activeVenue === idx ? "is-active" : "is-inactive"}`}
                data-index={idx}
                ref={(el) => (itemRefs.current[idx] = el)}
                onClick={() => scrollToStep(idx)}
              >
                <div className="story-item-content">
                  <span className="story-num">{step.number}</span>
                  <h3 className="story-title">{step.title}</h3>
                  <p className="story-desc">{step.description}</p>
                </div>
                <div className={`story-card-mobile-visual story-card-mobile-visual-${idx + 1}`}>
                  <div className="story-card-mobile-img-box">
                    <img
                      src={step.image}
                      alt={step.title}
                      className={`story-card-mobile-img story-img-${idx + 1}`}
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="crypto-story-visual-column" ref={visualColRef}>
            <div className="crypto-story-sticky" ref={stickyBoxRef} style={stickyStyle}>
              <div className={`crypto-story-image crypto-story-image-${activeVenue + 1}`}>
                <img
                  key={activeVenue}
                  src={VENUE_STEPS[activeVenue].image}
                  alt={VENUE_STEPS[activeVenue].title}
                  className={`story-img-active story-img-${activeVenue + 1}`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
          6. BUILT FOR HOW YOU USE CRYPTO (3 CARDS)
          ========================================================================== */}
      <section className="fin-inst-usecases-section">
        <div className="fin-inst-container">
          <div className="fin-inst-section-header">
            <h2 className="fin-inst-section-title">Built for how you use crypto.</h2>
          </div>

          <div className="fin-inst-usecases-grid">
            {USE_CASES.map((card, index) => (
              <div className="fin-inst-usecase-card" key={index} id={`fin-inst-usecase-${index + 1}`}>
                <div className="fin-inst-usecase-icon-box">
                  <img className="blur-icon-bg" src="/images/financial-institution-img/crypto_financial_back.png" alt="" />
                  <img
                    src={card.icon}
                    alt={card.title}
                    className={`fin-inst-usecase-icon ${card.iconLight ? "light_img" : ""}`}
                  />
                  {card.iconLight && (
                    <img
                      src={card.iconLight}
                      alt={card.title}
                      className="fin-inst-usecase-icon dark_img"
                    />
                  )}
                </div>
                <h3 className="fin-inst-usecase-title">{card.title}</h3>
                <p className="fin-inst-usecase-desc">{card.description}</p>
                <div className="finbtn_card">
                  <Link to={card.linkUrl} className="fin-inst-usecase-link">
                    <span>{card.linkText}</span>
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==========================================================================
          7. CTA BANNER SECTION
          ========================================================================== */}
      <section className="fin-inst-cta-section">
        <div className="fin-inst-container">
          <div className="fin-inst-cta-card">

            <div className="ctaouterbl">

              <h2 className="fin-inst-cta-title">Trade on an exchange built to last.</h2>
              <p className="fin-inst-cta-subtitle">
                Licensed. Separated. Secured. Get verified in minutes.
              </p>
              <div className="fin-inst-cta-actions">
                <Link to="/signup" className="fin-inst-btn-primary" id="fin-inst-cta-signup-btn">
                  <span>Open an account</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link to="/contact_us" className="fin-inst-btn-secondary" id="fin-inst-cta-contact-btn">
                  <span>Talk to the institutional desk</span>
                </Link>
              </div>
              <p className="fin-inst-cta-disclaimer">
                [Insert the risk warning your regulator requires, word for word.]
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
